// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import {useFluxStore} from 'client/framework';
import React, {useState} from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';
import {withRouter} from 'react-router';

import {Button, Dialogue, Icon, Modal, Text} from '@albert-io/atomic';

import {local} from 'lib/StorageUtil';
import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';
import sessionStore from 'client/Session/SessionStore';

type DowntimeType = {
  time: string;
  duration: string;
};

interface MaintenanceModalProps {
  location: {
    pathname?: string;
    query?: {
      DEBUG?: 'true' | 'false';
    };
  };
  downtimes: DowntimeType[];
  daysToShowBefore: number;
}

const formatRules = 'MMMM D, YYYY [at] h:mma z';

const routeOfLastAppearance = 'ALBERT_MAINTENANCE_ROUTE_OF_LAST_APPEARANCE';
const timeOfLastAppearance = 'ALBERT_TIME_OF_LAST_APPEARANCE';

const modalStateKey = 'albert.maintenance-modal';

function MaintenanceModal({location = {}, downtimes, daysToShowBefore = 5}: MaintenanceModalProps) {
  const hasSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  const {pathname = '', query = {}} = location;

  // Persisting the acknowledgement is the response to the user clicking OK/close, so it lives in
  // the event handler instead of an effect reacting to the flag. hasAcknowledged then only has to
  // hide the modal for this mount; the persisted state keeps it hidden across the 6h window.
  const handleAcknowledge = () => {
    const state = {
      [routeOfLastAppearance]: pathname,
      [timeOfLastAppearance]: systemTimeOffsetStore.getCurrentTime().valueOf()
    };
    local.set(modalStateKey, state);
    setHasAcknowledged(true);
  };

  const sortedDowntimeObjects = [...downtimes].sort((a, b) => {
    if (a.time === b.time) {
      return 0;
    }
    return moment(a.time).isBefore(moment(b.time)) ? -1 : 1;
  });

  const nextMaintenancePeriodObj = sortedDowntimeObjects.find((downtimeObj) =>
    systemTimeOffsetStore.getCurrentTime().isBefore(downtimeObj.time)
  );

  if (!nextMaintenancePeriodObj) {
    return null;
  }

  const nextMaintenancePeriodTime = nextMaintenancePeriodObj.time;
  const nextMaintenancePeriodDuration = nextMaintenancePeriodObj.duration;

  const isNextPeriodWithinTimeToShow =
    moment(nextMaintenancePeriodTime).diff(systemTimeOffsetStore.getCurrentTime(), 'days') <
      daysToShowBefore || query.DEBUG === 'true';

  const modalState = local.get(modalStateKey, {});
  const storedState = typeof modalState === 'object' && modalState !== null ? modalState : {};
  const storedTime = timeOfLastAppearance in storedState ? storedState[timeOfLastAppearance] : 0;
  const lastAppearedAt = typeof storedTime === 'number' ? storedTime : 0;
  const sixHoursHaveElapsed =
    query.DEBUG === 'true'
      ? moment(lastAppearedAt).add(30, 'seconds')
      : moment(lastAppearedAt).add(6, 'hours').isBefore(systemTimeOffsetStore.getCurrentTime());
  const routesMatch =
    routeOfLastAppearance in storedState && storedState[routeOfLastAppearance] === pathname;

  const showModal =
    !hasAcknowledged && !routesMatch && sixHoursHaveElapsed && isNextPeriodWithinTimeToShow;

  const lastDowntime = sortedDowntimeObjects[sortedDowntimeObjects.length - 1].time;

  // Skip modal for E2E tests (albert_version cookie set by test utilities)
  if (typeof document !== 'undefined' && document.cookie.includes('albert_version=disabled')) {
    return null;
  }

  if (!hasSession || systemTimeOffsetStore.getCurrentTime().isAfter(moment(lastDowntime))) {
    return null;
  }

  if (!showModal) {
    return null;
  }

  return (
    <Modal
      ariaLabel='Albert will be temporarily unavailable for upcoming maintenance'
      dismissable={false}
      handleClose={handleAcknowledge}
      role='alertdialog'
    >
      {({modalContentStyle}) => (
        <Dialogue
          className={classnames(modalContentStyle, 'text-center content-secondary')}
          hideCloseBtn
          inModal
        >
          <Icon className='fa-3x' icon={['far', 'construction']} />
          <Dialogue.Title align='center' className='my-x4'>
            Upcoming Maintenance
          </Dialogue.Title>
          <Dialogue.Body>
            <Text as='p' className='mb-x3 block' color='inherit'>
              Maintenance is scheduled for:
            </Text>
            <Text as='p' bold className='mb-x3 block' color='inherit'>
              {moment(nextMaintenancePeriodTime).tz(moment.tz.guess()).format(formatRules)}
            </Text>
            <Text as='p' color='inherit'>
              The entire site will undergo maintenance and be inaccessible for{' '}
              {nextMaintenancePeriodDuration}.
            </Text>
          </Dialogue.Body>
          <Dialogue.BtnGroup className='w-full'>
            <Button onClick={handleAcknowledge}>OK</Button>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
}

export default withRouter(MaintenanceModal);
